import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, resetUserPassword, resetUserMFA, getPermissionsList, updateUserPermissions, sendUserCredentials } from '../lib/api';
import { Button } from '../components/ui/button';
import { Loader2, Plus, Shield } from 'lucide-react';
import { toast } from 'sonner';

import { UsersTable } from '../components/users/UsersTable';
import { RolesPermissionsCard } from '../components/users/RolesPermissionsCard';
import { UserFormDialog } from '../components/users/UserFormDialog';
import { PermissionsDialog } from '../components/users/PermissionsDialog';
import { OperatorsManager } from '../components/users/OperatorsManager';

const emptyUser = {
  name: '', email: '', password: '', role: 'operator', phone: '', permissions: {}, page_permissions: {},
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyUser);
  const [saving, setSaving] = useState(false);
  const [systemPermissions, setSystemPermissions] = useState({});
  const [permissionCategories, setPermissionCategories] = useState({});
  const [userPermissions, setUserPermissions] = useState({});
  const [pagePermissions, setPagePermissions] = useState({});

  useEffect(() => { loadUsers(); loadPermissions(); }, []);

  const loadUsers = async () => {
    try { setUsers((await getUsers()).data); }
    catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const loadPermissions = async () => {
    try {
      const response = await getPermissionsList();
      setSystemPermissions(response.data.permissions || {});
      setPermissionCategories(response.data.categories || {});
    } catch (error) { console.error('Failed to load permissions list', error); }
  };

  const openCreateDialog = () => { setEditingUser(null); setFormData(emptyUser); setDialogOpen(true); };

  const openEditDialog = (user) => {
    setEditingUser(user);
    let perms = user.permissions || {};
    if (Array.isArray(perms)) perms = perms.reduce((acc, p) => ({ ...acc, [p]: true }), {});
    setFormData({
      name: user.name || '', email: user.email || '', password: '',
      role: user.role || 'operator', phone: user.phone || '',
      permissions: perms, page_permissions: user.page_permissions || {},
    });
    setDialogOpen(true);
  };

  const openPermissionsDialog = (user) => {
    setEditingUser(user);
    let perms = user.permissions || {};
    if (Array.isArray(perms)) perms = perms.reduce((acc, p) => ({ ...acc, [p]: true }), {});
    setUserPermissions(perms);
    setPagePermissions(user.page_permissions || {});
    setPermissionsDialogOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await updateUserPermissions(editingUser.id, { permissions: userPermissions, page_permissions: pagePermissions });
      toast.success('Permissions updated successfully');
      setPermissionsDialogOpen(false); loadUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update permissions'); }
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) { toast.error('Name and email are required'); return; }
    if (!editingUser && !formData.password) { toast.error('Password is required for new users'); return; }
    setSaving(true);
    try {
      if (editingUser) {
        await updateUser(editingUser.id, { name: formData.name, role: formData.role, phone: formData.phone, permissions: formData.permissions });
        toast.success('User updated');
      } else {
        await createUser(formData);
        toast.success('User created');
      }
      setDialogOpen(false); loadUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save user'); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async (user) => {
    if (!confirm(`Reset password for ${user.name}?`)) return;
    try { const response = await resetUserPassword(user.id); toast.success(`Password reset. Temporary: ${response.data.temporary_password}`); }
    catch { toast.error('Failed to reset password'); }
  };

  const handleResetMFA = async (user) => {
    if (!confirm(`Reset MFA for ${user.name}? They will need to re-enroll.`)) return;
    try { await resetUserMFA(user.id); toast.success('MFA reset successfully'); loadUsers(); }
    catch { toast.error('Failed to reset MFA'); }
  };

  const handleToggleStatus = async (user) => {
    try { await updateUser(user.id, { is_active: !user.is_active }); toast.success(user.is_active ? 'User disabled' : 'User enabled'); loadUsers(); }
    catch { toast.error('Failed to update user'); }
  };

  const handleSendCredentials = async (user) => {
    if (!confirm(`Send login credentials to ${user.email}? This will generate a new temporary password.`)) return;
    try {
      const response = await sendUserCredentials(user.id);
      toast.success(response.data.message || 'Credentials sent');
      if (response.data.email_error) {
        toast.info(`Temp password: ${response.data.temporary_password}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send credentials');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium">User Management</h2>
          <p className="text-sm text-muted-foreground">Manage user accounts and permissions</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="btn-add-user">
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      <UsersTable
        users={users}
        onCreateClick={openCreateDialog}
        onEditClick={openEditDialog}
        onPermissionsClick={openPermissionsDialog}
        onResetPassword={handleResetPassword}
        onResetMFA={handleResetMFA}
        onToggleStatus={handleToggleStatus}
        onSendCredentials={handleSendCredentials}
      />

      <RolesPermissionsCard
        systemPermissions={systemPermissions}
        permissionCategories={permissionCategories}
      />

      <OperatorsManager />

      <UserFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingUser={editingUser}
        formData={formData}
        setFormData={setFormData}
        saving={saving}
        onSave={handleSave}
      />

      <PermissionsDialog
        open={permissionsDialogOpen}
        onOpenChange={setPermissionsDialogOpen}
        editingUser={editingUser}
        userPermissions={userPermissions}
        setUserPermissions={setUserPermissions}
        pagePermissions={pagePermissions}
        setPagePermissions={setPagePermissions}
        systemPermissions={systemPermissions}
        permissionCategories={permissionCategories}
        saving={saving}
        onSave={handleSavePermissions}
      />
    </div>
  );
}
